// ═══════════════════════════════════════════════════════════════
// WorkspacePicker.jsx — verticalstack workspace selector
// Path: src/components/WorkspacePicker.jsx
//
// S31 LAYER 1 (Apr 27, 2026) — NEW FILE for sandbox-schema migration.
//
// PURPOSE
//   Shown after login when user is a member of 2+ workspaces and no
//   currentWorkspaceId is set in useWorkspace state. Single-workspace
//   users skip this entirely (auto-selected by useWorkspace).
//
// USER STORY
//   Chris logs in → useAuth hydrates 3 workspaces (rr / a1 / rop) →
//   useWorkspace finds no localStorage selection → renders this picker
//   → Chris clicks Red Oak Partners → setCurrentWorkspaceId('rop') →
//   Shell loads scoped to Red Oak.
//
// STYLING
//   Neutral professional formatting per memory #30 (branding deferred).
//   Inline styles only (no external CSS dependency, no theme system yet).
//   Colors limited to grayscale + the existing inline navy/teal already
//   present in App.js fallback branches (preserved verbatim per Row 10).
// ═══════════════════════════════════════════════════════════════

import React from 'react';
import { useAuth } from '../hooks/useAuth';
import { useWorkspace } from '../hooks/useWorkspace';
import { getRolesLabel, getVerticalLabel } from '../config/roles';

// ── Inline style objects (kept top-level for readability) ─────────────────

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#F8F9FA',
    padding: '48px 24px',
    fontFamily: 'Arial, sans-serif',
  },
  container: {
    maxWidth: 880,
    margin: '0 auto',
  },
  header: {
    marginBottom: 32,
    textAlign: 'center',
  },
  title: {
    fontSize: 28,
    fontWeight: 700,
    color: '#003060',
    margin: 0,
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 15,
    color: '#6B7280',
    margin: 0,
  },
  cardList: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: 20,
  },
  card: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #E5E7EB',
    borderRadius: 8,
    padding: 24,
    cursor: 'pointer',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    textAlign: 'left',
    width: '100%',
    fontFamily: 'inherit',
  },
  cardHover: {
    borderColor: '#003060',
    boxShadow: '0 4px 12px rgba(0, 48, 96, 0.08)',
    transform: 'translateY(-2px)',
  },
  cardName: {
    fontSize: 18,
    fontWeight: 700,
    color: '#003060',
    margin: 0,
  },
  cardMeta: {
    fontSize: 13,
    color: '#6B7280',
    margin: 0,
    lineHeight: 1.5,
  },
  cardRoleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 4,
  },
  badge: {
    display: 'inline-block',
    backgroundColor: '#F1F5F9',
    color: '#003060',
    fontSize: 12,
    fontWeight: 600,
    padding: '4px 8px',
    borderRadius: 4,
    letterSpacing: 0.2,
  },
  emptyState: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #E5E7EB',
    borderRadius: 8,
    padding: 32,
    textAlign: 'center',
    color: '#6B7280',
  },
  loadingState: {
    textAlign: 'center',
    color: '#6B7280',
    padding: 48,
    fontSize: 15,
  },
  footer: {
    marginTop: 32,
    textAlign: 'center',
    fontSize: 12,
    color: '#9CA3AF',
  },
};

// ── Card subcomponent (handles its own hover state) ───────────────────────

function WorkspaceCard({ workspace, onSelect }) {
  const [isHovered, setIsHovered] = React.useState(false);

  const cardStyle = {
    ...styles.card,
    ...(isHovered ? styles.cardHover : {}),
  };

  const verticalLabel = workspace.verticalType
    ? getVerticalLabel(workspace.verticalType)
    : 'Property Management';

  const planLabel = workspace.plan ? ` · ${workspace.plan}` : '';

  return (
    <button
      type="button"
      style={cardStyle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onSelect(workspace.workspaceId)}
      aria-label={`Open ${workspace.workspaceName} workspace`}
    >
      <h2 style={styles.cardName}>{workspace.workspaceName}</h2>
      <p style={styles.cardMeta}>
        {verticalLabel}{planLabel}
      </p>
      <div style={styles.cardRoleRow}>
        {workspace.roles && workspace.roles.length > 0 ? (
          <span style={styles.badge}>{getRolesLabel(workspace.roles)}</span>
        ) : (
          <span style={styles.badge}>No role assigned</span>
        )}
        {workspace.isHomeWorkspace && (
          <span style={{ ...styles.badge, backgroundColor: '#FEF3C7', color: '#92400E' }}>
            Home
          </span>
        )}
      </div>
    </button>
  );
}

// ── Main component ───────────────────────────────────────────────────────

export default function WorkspacePicker() {
  const { profile } = useAuth();
  const { availableWorkspaces, setCurrentWorkspaceId, loading } = useWorkspace();

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={styles.loadingState}>Loading your workspaces…</div>
        </div>
      </div>
    );
  }

  const userName = profile?.displayName || profile?.email || 'there';

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <header style={styles.header}>
          <h1 style={styles.title}>Choose a workspace</h1>
          <p style={styles.subtitle}>
            Welcome back, {userName}. Select which workspace you'd like to work in.
          </p>
        </header>

        {availableWorkspaces.length === 0 ? (
          <div style={styles.emptyState}>
            <p style={{ fontSize: 16, color: '#003060', marginTop: 0 }}>
              No workspaces yet
            </p>
            <p style={{ marginBottom: 0 }}>
              Your account isn't a member of any workspace. Contact verticalstack
              at <a
                href="mailto:hello@verticalstack.ai"
                style={{ color: '#00B8D0' }}
              >hello@verticalstack.ai</a>.
            </p>
          </div>
        ) : (
          <div style={styles.cardList}>
            {availableWorkspaces.map((ws) => (
              <WorkspaceCard
                key={ws.workspaceId}
                workspace={ws}
                onSelect={setCurrentWorkspaceId}
              />
            ))}
          </div>
        )}

        <div style={styles.footer}>
          verticalstack · stack more, manage less
        </div>
      </div>
    </div>
  );
}
